/**
 * Phase 15.7 — Memos Browser
 *
 * Single page listing every Issue and Return memo across goldsmith and
 * vendor flows, with rich filters and direct links to memo PDFs.
 */
import React, { useEffect, useMemo, useState } from 'react';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Skeleton } from '@/components/ui/skeleton';
import { DatePicker } from '@/components/ui/date-picker';
import { ArrowLeft, FileText, Send, ArrowDownToLine, Building2, Hammer, Gem, Coins, Calendar, X, Filter, Sparkles, Circle, User } from 'lucide-react';
import { formatINR, formatDateTime, formatGrams, formatCarats } from '@/lib/format';
import { useReferenceConfig } from '@/hooks/useReferenceConfig';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';

export default function Memos() {
  const navigate = useNavigate();
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell
      title="Memos"
      subtitle="All Issue & Return memos across goldsmiths and vendors — filter by period, party, material and memo type."
      right={
        <Button variant="outline" onClick={() => navigate(-1)} className="h-9" data-testid="memos-back-button">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
      }
    >
      <CompanyGate>
        <MemosContent companyId={effectiveCompanyId} />
      </CompanyGate>
    </AppShell>
  );
}

function MemosContent({ companyId }) {
  const [filter, setFilter] = useState({
    from_date: '',
    to_date: '',
    party_type: 'all',
    party_id: '',
    material_type: 'all',
    memo_type: 'both',
    reference_no: '',
  });
  const [goldsmiths, setGoldsmiths] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [refOptions, setRefOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const { enabled: refEnabled, label: refLabel } = useReferenceConfig(companyId);

  // Load parties once per company
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const [g, v] = await Promise.all([
          api.get('/masters/goldsmiths', { params: { company_id: companyId } }),
          api.get('/masters/vendors', { params: { company_id: companyId } }),
        ]);
        if (!cancelled) {
          setGoldsmiths(g.data || []);
          setVendors(v.data || []);
        }
      } catch (e) {
        // Party master (goldsmith / vendor) is only a filter aid. Some roles
        // (e.g. Sales Person / Sales Manager) lack the masters permission —
        // swallow the error silently so the memos view still loads for them.
        if (!cancelled) { setGoldsmiths([]); setVendors([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  // Wave 4.x — Load distinct running-numbers (Page Nos) for the searchable
  // Page No. filter. Only companies with running numbers enabled get any
  // options (the endpoint returns [] otherwise), so the filter self-hides.
  useEffect(() => {
    if (!companyId || !refEnabled) { setRefOptions([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/orders/reference-filter-options', { params: { company_id: companyId } });
        if (!cancelled) setRefOptions(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setRefOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, refEnabled]);

  // Load memos whenever filters change
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    const params = { company_id: companyId };
    if (filter.from_date) params.from_date = filter.from_date;
    if (filter.to_date) params.to_date = filter.to_date;
    if (filter.party_type && filter.party_type !== 'all') params.party_type = filter.party_type;
    if (filter.party_id) params.party_id = filter.party_id;
    if (filter.material_type && filter.material_type !== 'all') params.material_type = filter.material_type;
    if (filter.memo_type && filter.memo_type !== 'both') params.memo_type = filter.memo_type;
    if (filter.reference_no) params.reference_no = filter.reference_no;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get('/reports/memos', { params });
        if (!cancelled) setRows(data || []);
      } catch (e) {
        if (!cancelled) toast.error(extractError(e, 'Failed to load memos'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, filter.from_date, filter.to_date, filter.party_type, filter.party_id, filter.material_type, filter.memo_type, filter.reference_no]);

  const partyOptions = useMemo(() => {
    if (filter.party_type === 'goldsmith') return goldsmiths.map((g) => ({ id: g.id, name: g.name }));
    if (filter.party_type === 'vendor') return vendors.map((v) => ({ id: v.id, name: v.name }));
    return [];
  }, [filter.party_type, goldsmiths, vendors]);

  // Reset party_id when party_type changes
  useEffect(() => { setFilter((f) => ({ ...f, party_id: '' })); }, [filter.party_type]);

  const setPreset = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setFilter((f) => ({ ...f, from_date: fmt(start), to_date: fmt(end) }));
  };
  const clearAll = () => setFilter({
    from_date: '', to_date: '', party_type: 'all', party_id: '', material_type: 'all', memo_type: 'both', reference_no: '',
  });

  // Summary aggregates for the result set
  const summary = useMemo(() => {
    let issued = 0; let returned = 0; let issuedVal = 0; let returnedVal = 0;
    for (const r of rows) {
      if (r.memo_type === 'issue') { issued += 1; issuedVal += Number(r.value || 0); }
      else if (r.memo_type === 'return') { returned += 1; returnedVal += Number(r.value || 0); }
    }
    return { issued, returned, issuedVal, returnedVal };
  }, [rows]);

  // Per-raw-material quantity totals split by memo direction (Issue / Return),
  // computed from the filtered rows. Respects the active material filter — when
  // a specific material is selected only that material is tallied, matching the
  // "based on the filtered data" ask. Gold is tallied in grams, carat-based
  // materials in carats + pieces.
  const materialTotals = useMemo(() => {
    const blank = () => ({
      gold: { grams: 0, grams_999: 0, present: false },
      diamond: { carats: 0, pieces: 0, present: false },
      color_stone: { carats: 0, pieces: 0, present: false },
      pearl: { carats: 0, pieces: 0, present: false },
    });
    const acc = { issue: blank(), return: blank() };
    const matFilter = filter.material_type;
    for (const r of rows) {
      const bucket = r.memo_type === 'issue' ? acc.issue : (r.memo_type === 'return' ? acc.return : null);
      if (!bucket) continue;
      const q = r.quantities || {};
      for (const mat of ['gold', 'diamond', 'color_stone', 'pearl']) {
        if (matFilter !== 'all' && matFilter !== mat) continue;
        const mq = q[mat];
        if (!mq) continue;
        if (mat === 'gold') {
          bucket.gold.grams += Number(mq.grams || 0);
          bucket.gold.grams_999 += Number(mq.grams_999 || 0);
          if (Number(mq.grams || 0) > 0) bucket.gold.present = true;
        } else {
          bucket[mat].carats += Number(mq.carats || 0);
          bucket[mat].pieces += Number(mq.pieces || 0);
          if (Number(mq.carats || 0) > 0 || Number(mq.pieces || 0) > 0) bucket[mat].present = true;
        }
      }
    }
    return acc;
  }, [rows, filter.material_type]);

  // Req#2 — Group memos BY ORDER for display. A memo can span multiple orders
  // (each memo line carries its own order), so it appears under every order it
  // touches. Memos with no linked order fall into a trailing "No linked order"
  // bucket. Group header shows Order No + Title (per the requested layout).
  const UNATTR_KEY = '__no_order__';
  // Req#2 / Wave 4.x — Group memos for display. A memo can span multiple
  // orders (each memo line carries its own order), so it appears under every
  // order it touches. Memos with no linked order fall into a trailing "No
  // linked order" bucket.
  //
  //   • Running-number companies (refEnabled): two levels —
  //       Page No + Customer  →  Order Form No  →  memos
  //     Orders that have no Page No are shown as their own order-level groups.
  //   • Otherwise: one level — Order Form No + Customer → memos.
  const grouping = useMemo(() => {
    const orderMap = new Map();
    const noOrder = { key: UNATTR_KEY, memos: [] };
    for (const r of rows) {
      const ords = Array.isArray(r.orders) ? r.orders : [];
      if (!ords.length) { noOrder.memos.push(r); continue; }
      for (const o of ords) {
        const ok = o.order_id || o.order_no || UNATTR_KEY;
        if (ok === UNATTR_KEY) { noOrder.memos.push(r); continue; }
        if (!orderMap.has(ok)) {
          orderMap.set(ok, {
            key: ok,
            order_id: o.order_id || null,
            order_no: o.order_no || '',
            title: o.title || '',
            reference_no: (o.reference_no != null ? o.reference_no : null),
            customer_id: o.customer_id || null,
            customer_name: o.customer_name || '',
            memos: [],
          });
        }
        orderMap.get(ok).memos.push(r);
      }
    }
    const orderBuckets = Array.from(orderMap.values());
    orderBuckets.sort((a, b) => (a.order_no || '').localeCompare(b.order_no || '', undefined, { numeric: true }));

    if (refEnabled) {
      const topMap = new Map();
      const unpaged = [];
      for (const ob of orderBuckets) {
        if (ob.reference_no == null || ob.reference_no === '') { unpaged.push(ob); continue; }
        const tk = `${ob.reference_no}||${ob.customer_id || ob.customer_name || ''}`;
        if (!topMap.has(tk)) {
          topMap.set(tk, {
            key: tk,
            reference_no: ob.reference_no,
            customer_id: ob.customer_id,
            customer_name: ob.customer_name,
            orders: [],
          });
        }
        topMap.get(tk).orders.push(ob);
      }
      const tops = Array.from(topMap.values());
      tops.sort((a, b) => String(a.reference_no).localeCompare(String(b.reference_no), undefined, { numeric: true }));
      return { mode: 'page', tops, unpaged, noOrder };
    }
    return { mode: 'order', orderBuckets, noOrder };
  }, [rows, refEnabled]);

  // ---- Group header renderers (full-width rows inside the memos table) ----
  const renderOrderHeader = (ob, opts = {}) => (
    <tr
      key={`oh-${ob.key}`}
      className={opts.sub ? 'bg-white' : 'bg-[var(--paper-warm)]'}
      data-testid={`memos-order-group-${ob.key}`}
    >
      <td colSpan={7} className={`px-4 py-2 ${opts.sub ? 'pl-9 border-t border-[var(--divider)]' : 'border-t-2 border-[var(--divider)]'}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <FileText size={13} className="text-[var(--gold-icon)]" />
          {ob.order_id ? (
            <Link
              to={`/orders/${ob.order_id}`}
              className="font-mono text-[12px] bg-[var(--diamond-bg)] text-[var(--diamond-icon)] px-2 py-0.5 rounded hover:underline"
              data-testid={`memos-order-group-no-${ob.key}`}
            >
              {ob.order_no || 'Order'}
            </Link>
          ) : (
            <span
              className="font-mono text-[12px] bg-[var(--diamond-bg)] text-[var(--diamond-icon)] px-2 py-0.5 rounded"
              data-testid={`memos-order-group-no-${ob.key}`}
            >
              {ob.order_no || 'Order'}
            </span>
          )}
          {ob.title && (
            <span className="text-[13px] font-medium text-[var(--ink-navy)]" data-testid={`memos-order-group-title-${ob.key}`}>
              {ob.title}
            </span>
          )}
          {!opts.hideCustomer && ob.customer_name && (
            <span className="inline-flex items-center gap-1 text-[12px] text-slate-600" data-testid={`memos-order-group-customer-${ob.key}`}>
              <User size={11} className="text-slate-400" />{ob.customer_name}
            </span>
          )}
          {!opts.sub && refEnabled && ob.reference_no != null && (
            <Link
              to={`/orders?reference_no=${ob.reference_no}`}
              className="font-mono text-[12px] bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded hover:underline"
              title={`${refLabel} ${ob.reference_no}`}
              data-testid={`memos-order-group-ref-${ob.key}`}
            >
              {refLabel} {ob.reference_no}
            </Link>
          )}
          <Badge className="bg-white border border-[var(--divider)] text-slate-600 text-[10px]">
            {ob.memos.length} memo(s)
          </Badge>
        </div>
      </td>
    </tr>
  );

  const renderPageHeader = (tg) => {
    const count = tg.orders.reduce((n, ob) => n + ob.memos.length, 0);
    return (
      <tr key={`ph-${tg.key}`} className="bg-[var(--paper-warm)]" data-testid={`memos-page-group-${tg.key}`}>
        <td colSpan={7} className="px-4 py-2.5 border-t-2 border-[var(--divider)]">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/orders?reference_no=${tg.reference_no}`}
              className="font-mono text-[12px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded hover:underline"
              title={`${refLabel} ${tg.reference_no}`}
              data-testid={`memos-page-group-ref-${tg.key}`}
            >
              {refLabel} {tg.reference_no}
            </Link>
            {tg.customer_name && (
              <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-[var(--ink-navy)]" data-testid={`memos-page-group-customer-${tg.key}`}>
                <User size={12} className="text-slate-400" />{tg.customer_name}
              </span>
            )}
            <Badge className="bg-white border border-[var(--divider)] text-slate-600 text-[10px]">
              {count} memo(s)
            </Badge>
          </div>
        </td>
      </tr>
    );
  };

  const renderMemoRows = (ob) => ob.memos.map((r, i) => (
    <MemoRow key={`${ob.key}-${r.link?.kind}-${r.memo_no}-${i}`} row={r} spanCount={(r.orders || []).length} />
  ));

  const renderNoOrder = (memos) => (
    <React.Fragment key="no-order">
      <tr className="bg-[var(--paper-warm)]" data-testid={`memos-order-group-${UNATTR_KEY}`}>
        <td colSpan={7} className="px-4 py-2 border-t-2 border-[var(--divider)]">
          <div className="flex items-center gap-2">
            <FileText size={13} className="text-slate-400" />
            <span className="text-[13px] font-semibold text-slate-500" data-testid="memos-order-group-unattributed">No linked order</span>
            <Badge className="bg-white border border-[var(--divider)] text-slate-600 text-[10px]">{memos.length} memo(s)</Badge>
          </div>
        </td>
      </tr>
      {memos.map((r, i) => (
        <MemoRow key={`no-order-${r.link?.kind}-${r.memo_no}-${i}`} row={r} spanCount={(r.orders || []).length} />
      ))}
    </React.Fragment>
  );

  return (
    <div className="space-y-5">
      {/* Filters card */}
      <Card className="p-5 sticky-filter-bar" data-testid="memos-filters-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-slate-500" />
            <div className="text-sm font-medium text-[var(--ink-navy)]">Filters</div>
          </div>
          <Button variant="ghost" size="sm" onClick={clearAll} data-testid="memos-clear-filters">
            <X size={12} className="mr-1" /> Clear
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          <div>
            <Label className="text-[11px] text-slate-500">From</Label>
            <DatePicker
              value={filter.from_date}
              onChange={(e) => setFilter({ ...filter, from_date: e.target.value })}
              className="h-9"
              data-testid="memos-filter-from"
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">To</Label>
            <DatePicker
              value={filter.to_date}
              onChange={(e) => setFilter({ ...filter, to_date: e.target.value })}
              className="h-9"
              data-testid="memos-filter-to"
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Memo Type</Label>
            <Select value={filter.memo_type} onValueChange={(v) => setFilter({ ...filter, memo_type: v })}>
              <SelectTrigger className="h-9" data-testid="memos-filter-memo-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Issue & Return</SelectItem>
                <SelectItem value="issue">Issue only</SelectItem>
                <SelectItem value="return">Return only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Party Type</Label>
            <Select value={filter.party_type} onValueChange={(v) => setFilter({ ...filter, party_type: v })}>
              <SelectTrigger className="h-9" data-testid="memos-filter-party-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All parties</SelectItem>
                <SelectItem value="goldsmith">Goldsmiths</SelectItem>
                <SelectItem value="vendor">Vendors</SelectItem>
                <SelectItem value="party">Sale Parties</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Party</Label>
            {(() => {
              const allLabel = `All ${filter.party_type === 'goldsmith' ? 'goldsmiths' : filter.party_type === 'vendor' ? 'vendors' : 'parties'}`;
              const items = [{ id: 'all', name: allLabel }, ...partyOptions];
              return (
                <SearchableSelect
                  items={items}
                  value={filter.party_id || 'all'}
                  onValueChange={(v) => setFilter({ ...filter, party_id: !v || v === 'all' ? '' : v })}
                  itemToLabel={(p) => p.name}
                  searchKeys={['name']}
                  placeholder={filter.party_type === 'all' ? 'Choose party type first' : 'All'}
                  searchPlaceholder="Search party…"
                  disabled={filter.party_type === 'all' || partyOptions.length === 0}
                  testId="memos-filter-party"
                />
              );
            })()}
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Material</Label>
            <Select value={filter.material_type} onValueChange={(v) => setFilter({ ...filter, material_type: v })}>
              <SelectTrigger className="h-9" data-testid="memos-filter-material-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All materials</SelectItem>
                <SelectItem value="gold">Gold</SelectItem>
                <SelectItem value="diamond">Diamonds</SelectItem>
                <SelectItem value="color_stone">Color Stones</SelectItem>
                <SelectItem value="pearl">Pearls</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Wave 4.x — Page No. (running number) filter — only for companies
              that use running numbers. Searchable dropdown of existing page
              numbers; exact match, mirrors the CAD report's Page No filter. */}
          {refEnabled && (
            <div>
              <Label className="text-[11px] text-slate-500">{refLabel}</Label>
              {(() => {
                const items = [{ id: 'all', name: `All ${refLabel}` }, ...refOptions.map((n) => ({ id: String(n), name: String(n) }))];
                return (
                  <SearchableSelect
                    items={items}
                    value={filter.reference_no ? String(filter.reference_no) : 'all'}
                    onValueChange={(v) => setFilter((f) => ({ ...f, reference_no: !v || v === 'all' ? '' : v }))}
                    itemToLabel={(p) => p.name}
                    searchKeys={['name']}
                    placeholder={`All ${refLabel}`}
                    searchPlaceholder={`Search ${refLabel}…`}
                    disabled={refOptions.length === 0}
                    emptyText="No page numbers yet"
                    testId="memos-filter-reference"
                  />
                );
              })()}
            </div>
          )}
        </div>
        {/* Quick date presets */}
        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          <span className="text-[11px] text-slate-500 mr-1">Quick range:</span>
          {[7, 30, 90, 180, 365].map((d) => (
            <Button
              key={d}
              variant="outline"
              size="sm"
              className="h-7 text-[11px] px-2"
              onClick={() => setPreset(d)}
              data-testid={`memos-preset-${d}d`}
            >
              <Calendar size={11} className="mr-1" />Last {d}d
            </Button>
          ))}
        </div>
      </Card>

      {/* Summary row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="memos-summary-row">
        <SummaryTile
          icon={<Send size={14} />}
          label="Issue Memos"
          value={summary.issued}
          sub={formatINR(summary.issuedVal)}
          tone="navy"
          testid="memos-summary-issued"
        />
        <SummaryTile
          icon={<ArrowDownToLine size={14} />}
          label="Return Memos"
          value={summary.returned}
          sub={formatINR(summary.returnedVal)}
          tone="amber"
          testid="memos-summary-returned"
        />
        <SummaryTile
          icon={<FileText size={14} />}
          label="Total Memos"
          value={rows.length}
          sub={formatINR(summary.issuedVal + summary.returnedVal)}
          tone="slate"
          testid="memos-summary-total"
        />
        <SummaryTile
          icon={<Filter size={14} />}
          label="Active Filters"
          value={countActiveFilters(filter)}
          sub={countActiveFilters(filter) === 0 ? 'No filters applied' : 'Refined view'}
          tone="violet"
          testid="memos-summary-filters"
        />
      </div>

      {/* Per-material quantity tiles — total Issue vs Return quantity for each
          raw material, honouring the active filters (grams for gold, carats +
          pieces for diamonds / colour stones / pearls). */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="memos-material-totals">
        <MaterialQtyTile
          title="Materials Issued"
          icon={<Send size={15} />}
          tone="navy"
          totals={materialTotals.issue}
          testid="memos-issued-materials"
        />
        <MaterialQtyTile
          title="Materials Returned"
          icon={<ArrowDownToLine size={15} />}
          tone="amber"
          totals={materialTotals.return}
          testid="memos-returned-materials"
        />
      </div>

      {/* Memos table */}
      <Card className="p-0 overflow-hidden" data-testid="memos-results-card">
        <div className="px-5 py-3 border-b border-[var(--divider)] flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-[var(--ink-navy)]">Memos</div>
            <div className="text-xs text-slate-500">Showing {rows.length} memo(s){loading ? ' · loading…' : ''}</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="memos-table">
            <thead className="bg-[var(--paper-warm)] text-[var(--ink-secondary)] text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Date</th>
                <th className="text-left px-4 py-2 font-medium">Memo No</th>
                <th className="text-left px-4 py-2 font-medium">Type</th>
                <th className="text-left px-4 py-2 font-medium">Party</th>
                <th className="text-left px-4 py-2 font-medium">Material</th>
                <th className="text-right px-4 py-2 font-medium">Value</th>
                <th className="text-right px-4 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--divider)]">
              {loading && rows.length === 0 && (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <td key={`sk-${i}-${j}`} className="px-4 py-2"><Skeleton className="h-4 w-full" /></td>
                    ))}
                  </tr>
                ))
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500 text-sm" data-testid="memos-empty">No memos match the current filters.</td></tr>
              )}
              {!loading && grouping.mode === 'page' && (
                <>
                  {grouping.tops.map((tg) => (
                    <React.Fragment key={tg.key}>
                      {renderPageHeader(tg)}
                      {tg.orders.map((ob) => (
                        <React.Fragment key={ob.key}>
                          {renderOrderHeader(ob, { sub: true, hideCustomer: true })}
                          {renderMemoRows(ob)}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  ))}
                  {grouping.unpaged.map((ob) => (
                    <React.Fragment key={ob.key}>
                      {renderOrderHeader(ob)}
                      {renderMemoRows(ob)}
                    </React.Fragment>
                  ))}
                  {grouping.noOrder.memos.length > 0 && renderNoOrder(grouping.noOrder.memos)}
                </>
              )}
              {!loading && grouping.mode === 'order' && (
                <>
                  {grouping.orderBuckets.map((ob) => (
                    <React.Fragment key={ob.key}>
                      {renderOrderHeader(ob)}
                      {renderMemoRows(ob)}
                    </React.Fragment>
                  ))}
                  {grouping.noOrder.memos.length > 0 && renderNoOrder(grouping.noOrder.memos)}
                </>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function countActiveFilters(f) {
  let n = 0;
  if (f.from_date) n += 1;
  if (f.to_date) n += 1;
  if (f.party_type && f.party_type !== 'all') n += 1;
  if (f.party_id) n += 1;
  if (f.material_type && f.material_type !== 'all') n += 1;
  if (f.memo_type && f.memo_type !== 'both') n += 1;
  if (f.reference_no) n += 1;
  return n;
}

function SummaryTile({ icon, label, value, sub, tone, testid }) {
  const tones = {
    navy: 'bg-slate-50 border-slate-200 text-slate-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    slate: 'bg-white border-slate-200 text-slate-800',
    violet: 'bg-violet-50 border-violet-200 text-violet-900',
  };
  return (
    <div className={`rounded-lg border ${tones[tone] || tones.slate} p-4`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-500">
        {icon} <span>{label}</span>
      </div>
      <div className="mt-1.5 font-mono tabular-nums text-2xl text-[var(--ink-navy)]">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

// Raw-material presentation metadata for the quantity tiles.
const MATERIAL_META = {
  gold: { label: 'Gold', Icon: Coins, color: 'var(--gold-icon)', bg: 'var(--gold-bg, #FBF3E0)' },
  diamond: { label: 'Diamonds', Icon: Gem, color: 'var(--diamond-icon)', bg: 'var(--diamond-bg, #EEF2FF)' },
  color_stone: { label: 'Color Stones', Icon: Sparkles, color: 'var(--colorstone-icon, #C84A6F)', bg: 'var(--colorstone-bg, #FBEAF0)' },
  pearl: { label: 'Pearls', Icon: Circle, color: 'var(--pearl-icon, #6B7280)', bg: 'var(--pearl-bg, #F1F5F9)' },
};

/**
 * MaterialQtyTile — shows the total quantity of each raw material for one memo
 * direction (Issue or Return). Gold is shown in grams (with the fine 999
 * weight beneath); diamonds / colour stones / pearls in carats + pieces.
 */
function MaterialQtyTile({ title, icon, tone, totals, testid }) {
  const toneRing = tone === 'navy' ? 'border-l-[var(--ink-navy)]' : 'border-l-amber-400';
  const toneText = tone === 'navy' ? 'text-[var(--ink-navy)]' : 'text-amber-600';
  const items = [];
  if (totals.gold.present) {
    items.push({ key: 'gold', qty: `${formatGrams(totals.gold.grams)} g`, sub: `999: ${formatGrams(totals.gold.grams_999)} g` });
  }
  for (const mat of ['diamond', 'color_stone', 'pearl']) {
    if (totals[mat].present) {
      items.push({ key: mat, qty: `${formatCarats(totals[mat].carats)} ct`, sub: `${totals[mat].pieces} pcs` });
    }
  }
  return (
    <Card className={`p-4 border-l-4 ${toneRing}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <span className={toneText}>{icon}</span>
        <div className="text-sm font-semibold text-[var(--ink-navy)]">{title}</div>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-400 py-5 text-center" data-testid={`${testid}-empty`}>No material movements in this view</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {items.map((it) => {
            const meta = MATERIAL_META[it.key];
            const Icon = meta.Icon;
            return (
              <div key={it.key} className="flex items-center gap-2.5 rounded-md border border-[var(--divider)] px-2.5 py-2 bg-white" data-testid={`${testid}-${it.key}`}>
                <span className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full" style={{ background: meta.bg }}>
                  <Icon size={15} style={{ color: meta.color }} />
                </span>
                <div className="min-w-0">
                  <div className="text-[11px] text-slate-500 leading-tight">{meta.label}</div>
                  <div className="font-mono tabular-nums text-[14px] text-[var(--ink-navy)] leading-tight" data-testid={`${testid}-${it.key}-qty`}>{it.qty}</div>
                  {it.sub && <div className="text-[10px] text-slate-400 leading-tight font-mono">{it.sub}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function MemoRow({ row, spanCount = 1 }) {
  const isIssue = row.memo_type === 'issue';
  const isVendor = row.party_type === 'vendor';
  const isParty = row.party_type === 'party';
  // Resolve link target — vendor_return is module-specific (Phase 16).
  let target = '#';
  if (row.link?.kind === 'goldsmith_issue' && row.link.id) target = `/issue-memos/${row.link.id}`;
  else if (row.link?.kind === 'goldsmith_return' && row.link.id) target = `/return-memos/${row.link.id}`;
  else if (row.link?.kind === 'vendor_return' && row.link.memo_no) {
    const mk = row.link.material_kind || row.material_kind || 'diamond';
    const moduleSlug = mk === 'color_stone' ? 'color-stones' : mk === 'pearl' ? 'pearls' : 'diamonds';
    target = `/return-memos/vendor/${encodeURIComponent(row.link.memo_no)}?module=${moduleSlug}`;
  }
  // Phase 18 — Purchase Return + Sale memos. The Memos PDFs/details for
  // these will be implemented in a follow-up; for now the link goes to the
  // Memos page itself (no-op), since the row already shows everything
  // needed.
  // (Once dedicated PDFs are built, route them here.)

  return (
    <tr className="hover:bg-slate-50" data-testid={`memos-row-${row.memo_no}`}>
      <td className="px-4 py-2.5 text-[12px] text-slate-600 whitespace-nowrap">{formatDateTime(row.date)}</td>
      <td className="px-4 py-2.5">
        <Link to={target} className="font-mono text-[12px] bg-[var(--diamond-bg)] text-[var(--diamond-icon)] px-2 py-0.5 rounded hover:underline" data-testid={`memos-row-link-${row.memo_no}`}>
          <FileText size={11} className="inline mr-1" />{row.memo_no}
        </Link>
        {spanCount > 1 && (
          <div className="text-[10px] text-amber-700 mt-0.5" title="This memo covers multiple orders; its value is shown in full under each">
            spans {spanCount} orders
          </div>
        )}
      </td>
      <td className="px-4 py-2.5">
        {row.link?.kind === 'purchase_return' ? (
          <Badge className="bg-amber-50 text-amber-800 border border-amber-200 text-[10px]" data-testid={`memos-row-type-pr-${row.memo_no}`}>
            <ArrowDownToLine size={10} className="mr-1" />Purchase Return
          </Badge>
        ) : row.link?.kind === 'sale' ? (
          <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px]" data-testid={`memos-row-type-sale-${row.memo_no}`}>
            <Send size={10} className="mr-1" />Sale
          </Badge>
        ) : isIssue ? (
          <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px]"><Send size={10} className="mr-1" />Issue</Badge>
        ) : (
          <Badge className="bg-amber-50 text-amber-800 border border-amber-200 text-[10px]"><ArrowDownToLine size={10} className="mr-1" />Return</Badge>
        )}
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-1.5 text-sm text-slate-700">
          {isParty ? (
            <Hammer size={12} className="text-emerald-700 shrink-0" />
          ) : isVendor ? (
            <Building2 size={12} className="text-[var(--vendor-icon)] shrink-0" />
          ) : (
            <Hammer size={12} className="text-[var(--goldsmith-icon)] shrink-0" />
          )}
          <span className="truncate max-w-[180px]" title={row.party_name}>{row.party_name || '—'}</span>
        </div>
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2">
          {row.has_diamond && <Gem size={12} className="text-[var(--diamond-icon)] shrink-0" title="Diamonds" />}
          {row.has_color_stone && <Sparkles size={12} className="text-pink-500 shrink-0" />}
          {row.has_pearl && <Circle size={12} className="text-slate-500 shrink-0" />}
          {row.has_gold && <Coins size={12} className="text-[var(--gold-icon)] shrink-0" />}
          <span className="text-xs text-slate-600 truncate max-w-[300px]" title={row.material_summary}>{row.material_summary}</span>
        </div>
      </td>
      <td className="px-4 py-2.5 text-right font-mono tabular-nums text-slate-800 whitespace-nowrap">{formatINR(row.value || 0)}</td>
      <td className="px-4 py-2.5 text-right whitespace-nowrap">
        {target !== '#' ? (
          <Button asChild size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`memos-row-open-${row.memo_no}`}>
            <Link to={target}><FileText size={11} className="mr-1" />Open</Link>
          </Button>
        ) : (
          <span className="text-[11px] text-slate-400 italic" data-testid={`memos-row-no-pdf-${row.memo_no}`}>—</span>
        )}
      </td>
    </tr>
  );
}
